export const DEPENDENT_DEDUCTION_SECTION_ORDER = [
  'employee',
  'spouse',
  'dependents',
  'specialStatus',
  'otherIncomeEarner',
] as const;

export type DependentDeductionSectionId = (typeof DEPENDENT_DEDUCTION_SECTION_ORDER)[number];

export type SectionNavStatus = 'locked' | 'current' | 'available' | 'completed';

/** Left accent bar color per section (matches UI mockup). */
export const SECTION_ACCENT_COLOR: Record<DependentDeductionSectionId, string> = {
  employee: '#5B9BD5',
  spouse: '#ED7D31',
  dependents: '#E07A9A',
  specialStatus: '#70AD47',
  otherIncomeEarner: '#8064A2',
};

export function sectionLabelKey(id: DependentDeductionSectionId): string {
  return `submitDocuments.dependentDeduction.sections.${id}.label`;
}

export function nextSectionId(
  current: DependentDeductionSectionId
): DependentDeductionSectionId | null {
  const idx = DEPENDENT_DEDUCTION_SECTION_ORDER.indexOf(current);
  if (idx < 0 || idx >= DEPENDENT_DEDUCTION_SECTION_ORDER.length - 1) return null;
  return DEPENDENT_DEDUCTION_SECTION_ORDER[idx + 1];
}

export function firstIncompleteSection(
  statuses: Record<DependentDeductionSectionId, SectionNavStatus>
): DependentDeductionSectionId | null {
  for (const id of DEPENDENT_DEDUCTION_SECTION_ORDER) {
    if (statuses[id] === 'available' || statuses[id] === 'current') return id;
  }
  return null;
}

export function initialSectionStatuses(): Record<DependentDeductionSectionId, SectionNavStatus> {
  return {
    employee: 'current',
    spouse: 'locked',
    dependents: 'locked',
    specialStatus: 'locked',
    otherIncomeEarner: 'locked',
  };
}

export type TaxDeclarationSectionFlags = {
  sectionEmployeeDone?: boolean;
  sectionSpouseDone?: boolean;
  sectionDependentsDone?: boolean;
  sectionSpecialDone?: boolean;
  sectionOtherIncomeDone?: boolean;
};

/** Restore section menu state from persisted tax_declaration flags. */
export function sectionStatusesFromFlags(
  flags: TaxDeclarationSectionFlags
): Record<DependentDeductionSectionId, SectionNavStatus> {
  const next = initialSectionStatuses();
  if (flags.sectionEmployeeDone) {
    next.employee = 'completed';
    next.spouse = 'available';
  }
  if (flags.sectionSpouseDone) {
    next.spouse = 'completed';
    next.dependents = 'available';
  }
  if (flags.sectionDependentsDone) {
    next.dependents = 'completed';
    next.specialStatus = 'available';
  }
  if (flags.sectionSpecialDone) {
    next.specialStatus = 'completed';
    next.otherIncomeEarner = 'available';
  }
  if (flags.sectionOtherIncomeDone) {
    next.otherIncomeEarner = 'completed';
  }
  return next;
}

/** After Section 1 is marked incomplete again (edit/undo), keep later completed sections. */
export function sectionStatusesAfterEmployeeIncomplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return {
    ...prev,
    employee: 'current',
    spouse: prev.spouse === 'completed' ? 'completed' : 'locked',
    dependents: prev.dependents === 'completed' ? 'completed' : 'locked',
    specialStatus: prev.specialStatus === 'completed' ? 'completed' : 'locked',
    otherIncomeEarner: prev.otherIncomeEarner === 'completed' ? 'completed' : 'locked',
  };
}

export function sectionStatusesAfterEmployeeComplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  const next = { ...prev, employee: 'completed' as SectionNavStatus };
  if (next.spouse === 'locked') next.spouse = 'available';
  return next;
}

export function sectionStatusesAfterSpouseIncomplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return {
    ...prev,
    spouse: 'current',
    dependents: prev.dependents === 'completed' ? 'completed' : 'locked',
    specialStatus: prev.specialStatus === 'completed' ? 'completed' : 'locked',
    otherIncomeEarner: prev.otherIncomeEarner === 'completed' ? 'completed' : 'locked',
  };
}

export function sectionStatusesAfterSpouseComplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  const next = { ...prev, spouse: 'completed' as SectionNavStatus };
  if (next.dependents === 'locked') next.dependents = 'available';
  return next;
}

export function sectionStatusesAfterDependentsIncomplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return {
    ...prev,
    dependents: 'current',
    specialStatus: prev.specialStatus === 'completed' ? 'completed' : 'locked',
    otherIncomeEarner: prev.otherIncomeEarner === 'completed' ? 'completed' : 'locked',
  };
}

export function sectionStatusesAfterDependentsComplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  const next = { ...prev, dependents: 'completed' as SectionNavStatus };
  if (next.specialStatus === 'locked') next.specialStatus = 'available';
  return next;
}

export function sectionStatusesAfterSpecialIncomplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return {
    ...prev,
    specialStatus: 'current',
    otherIncomeEarner: prev.otherIncomeEarner === 'completed' ? 'completed' : 'locked',
  };
}

export function sectionStatusesAfterSpecialComplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  const next = { ...prev, specialStatus: 'completed' as SectionNavStatus };
  if (next.otherIncomeEarner === 'locked') next.otherIncomeEarner = 'available';
  return next;
}

export function sectionStatusesAfterOtherIncomeIncomplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return { ...prev, otherIncomeEarner: 'current' };
}

export function sectionStatusesAfterOtherIncomeComplete(
  prev: Record<DependentDeductionSectionId, SectionNavStatus>
): Record<DependentDeductionSectionId, SectionNavStatus> {
  return { ...prev, otherIncomeEarner: 'completed' as SectionNavStatus };
}

export function allSectionsComplete(
  statuses: Record<DependentDeductionSectionId, SectionNavStatus>
): boolean {
  return DEPENDENT_DEDUCTION_SECTION_ORDER.every((id) => statuses[id] === 'completed');
}
